/* eslint-disable */
/** @jsx h */
import { Fragment, h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { clearCart } from '../objects/cart/ajax_cart';
import DraftOrderConfirmationModal from './DraftOrderModal';
import API_ENDPOINTS from '../config/api.js';
import { proxyFetch } from '../objects/proxyAuth.js';

function DraftOrders() {
  const [draftOrders, setDraftOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  // A draft that can be opened, but not exactly as saved — held here until the rep
  // has seen what will be left out (COB2B-801 issue 1).
  const [pendingOpen, setPendingOpen] = useState(null);
  // A draft with nothing addable at all. Shown beside the list rather than through
  // setError, which replaces the whole table.
  const [blockedMessage, setBlockedMessage] = useState(null);

  useEffect(() => {
    // Get company ID from DOM element
    const dataElement = document.getElementById('sales-rep-data');
    const currentCompanyId = dataElement?.dataset?.currentCompanyId;

    if (!currentCompanyId) {
      setError('Company ID not found. Please ensure you are logged in as an account manager.');
      return;
    }

    setCompanyId(currentCompanyId);
    fetchDraftOrders(currentCompanyId);
  }, []);

  const fetchDraftOrders = async (companyId) => {
    setLoading(true);
    try {
      const response = await proxyFetch(API_ENDPOINTS.DRAFT_ORDERS_BY_COMPANY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // Company resolved server-side from the signed customer id.
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setDraftOrders(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatStatus = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'OPEN':
        return 'Open';
      case 'INVOICE_SENT':
        return 'Invoice Sent';
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'status-completed';
      case 'OPEN':
        return 'status-open';
      case 'INVOICE_SENT':
        return 'status-invoice-sent';
      default:
    }
  };

  /**
   * Delete an open draft order. `require_open` makes the backend reject anything
   * that is no longer open — the list may be stale by the time this is clicked.
   */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleteError(null);
    try {
      const response = await proxyFetch(API_ENDPOINTS.DELETE_DRAFT_ORDER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draft_id: deleteTarget.id, require_open: true }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.error) {
        console.error('[DraftOrders] DELETE_DRAFT_ORDER failed', {
          status: response.status,
          body: result,
        });
        throw new Error(
          response.status === 409
            ? 'Only open draft orders can be deleted — this one is no longer open.'
            : 'Could not delete this draft order. Please try again.',
        );
      }

      setDeleteTarget(null);
      await fetchDraftOrders(companyId);
    } catch (err) {
      setDeleteError(err.message);
      setDeleteTarget(null);
    }
  };

  /**
   * COB2B-801 issue 1: Shopify validates a batch /cart/add.js atomically — a single
   * unsellable line fails the whole request and nothing is added, which is why a draft
   * containing an out-of-stock product could not be opened at all. Confirmed with
   * Shopify support (2026-09-21); /cart/update.js behaves identically, so there is no
   * best-effort mode to switch to.
   *
   * The draft's lines are therefore sorted here, from the availability the backend now
   * returns, BEFORE the cart is touched — only sellable lines are sent.
   *
   * `availableForSale` is only trusted when it is explicitly false: an older backend
   * that doesn't return it leaves every line addable, i.e. exactly today's behaviour,
   * so the theme degrades safely if it ships ahead of the API change.
   */
  const partitionLines = (edges) => {
    const addable = [];
    const unavailable = [];
    const capped = [];

    edges.forEach((edge) => {
      const node = edge?.node;
      const variant = node?.variant;
      const label = (node?.name && String(node.name).trim()) || 'A product';

      // A missing variant means the product is gone from the catalogue rather than
      // merely out of stock. Different cause, same consequence for the rep, so it is
      // reported alongside instead of aborting the whole open as it used to.
      if (!variant?.id) {
        unavailable.push({ label, reason: 'no longer available for purchase' });
        return;
      }

      if (variant.availableForSale === false) {
        unavailable.push({ label, reason: 'out of stock' });
        return;
      }

      const requested = node.quantity;
      const available = variant.inventoryQuantity;
      // Untracked stock, or a policy that allows overselling, means there is no cap
      // to predict.
      const isLimited =
        variant.inventoryItem?.tracked &&
        variant.inventoryPolicy === 'DENY' &&
        typeof available === 'number';

      // Nothing sellable left, whatever availableForSale reported. Sending this would
      // fail the batch for every other line, so the partition must not contradict
      // itself by treating it as addable.
      if (isLimited && available <= 0) {
        unavailable.push({ label, reason: 'out of stock' });
        return;
      }

      if (isLimited && available < requested) {
        capped.push({ label, requested, available });
      }

      // The full quantity is still requested — Shopify caps a short line to what it
      // can fill and returns 200, so asking for everything gets the maximum available.
      addable.push({ id: variant.id.split('/').pop(), quantity: requested });
    });

    return { addable, unavailable, capped };
  };

  const reportOpenFailure = (error) => {
    console.error('[DraftOrders] load draft to cart — full error detail', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      cause: error?.cause,
    });
    console.error('Error loading draft to cart:', error);

    const reloadSuffix = ' [RELOAD]';
    const isNullIdTypeError =
      error?.name === 'TypeError' &&
      String(error?.message || '').includes("reading 'id'");

    let display;
    if (error.fromShopifyCart) {
      display = error.message + reloadSuffix;
    } else if (error.message.includes('Cannot find variant')) {
      display =
        'Cannot edit this draft order. One or more items are no longer available for purchase.' +
        reloadSuffix;
    } else if (isNullIdTypeError) {
      display =
        'One or more items in this draft could not be loaded. They may have been removed from the catalogue.' +
        reloadSuffix;
    } else {
      display =
        (error.message || 'Failed to load draft order. Please try again.') +
        reloadSuffix;
    }
    setError(display);

    setLoadingDraft(false);
  };

  /**
   * The only place the cart is mutated. Reached either directly (nothing to warn
   * about) or from the rep confirming the modal — never before the partition has
   * established there is something worth adding, so a failed open can no longer
   * leave the rep with an emptied cart and no draft.
   */
  const openDraftInCart = async ({ orderIdNumber, draftNameParam, addable }) => {
    await clearCart();

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: addable }),
    });

    if (!addResponse.ok) {
      const addErrorText = await addResponse.text();
      let addErrorParsed = addErrorText;
      try {
        addErrorParsed = JSON.parse(addErrorText);
      } catch {
        // leave as raw text
      }
      console.error('[DraftOrders] /cart/add.js failed', {
        status: addResponse.status,
        statusText: addResponse.statusText,
        body: addErrorParsed,
      });

      const shopifyMsg =
        typeof addErrorParsed === 'object' &&
        addErrorParsed !== null &&
        (addErrorParsed.message || addErrorParsed.description);

      let userMsg = shopifyMsg;
      if (!userMsg && addErrorText.includes('Cannot find variant')) {
        userMsg =
          'One or more items in this draft are no longer available for purchase.';
      }
      if (!userMsg) {
        userMsg = `Could not add items to the cart (${addResponse.status}).`;
      }

      const err = new Error(userMsg);
      err.fromShopifyCart = true;
      throw err;
    }

    // Redirect to cart with draft_id. draft_name is display-only — it lets the cart
    // header show the draft number before DRAFT_ORDER_BY_ID comes back. The leading
    // '#' is stripped here and added back when the header renders it.
    window.location.href = `/cart?draft_id=${orderIdNumber}${draftNameParam}`;
  };

  /**
   * Handle draft order click - work out what can be added, then populate cart
   */
  const handleDraftOrderClick = async (orderIdNumber) => {
    setLoadingDraft(true);
    setBlockedMessage(null);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      // Fetch full draft order data
      const response = await proxyFetch(API_ENDPOINTS.DRAFT_ORDER_BY_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_id: `gid://shopify/DraftOrder/${orderIdNumber}`,
        }),
      });

      if (!response.ok) {
        const errorBodyText = await response.text();
        let errorBodyParsed = errorBodyText;
        try {
          errorBodyParsed = JSON.parse(errorBodyText);
        } catch {
          // leave as raw text
        }
        console.error('[DraftOrders] DRAFT_ORDER_BY_ID failed', {
          status: response.status,
          statusText: response.statusText,
          body: errorBodyParsed,
        });
        throw new Error('Failed to fetch draft order');
      }

      const result = await response.json();
      const draftOrder = result.data;

      if (!draftOrder) {
        throw new Error('No draft order data returned from API');
      }

      if (!draftOrder.lineItems || !draftOrder.lineItems.edges) {
        throw new Error('Draft order has no line items');
      }

      const { addable, unavailable, capped } = partitionLines(
        draftOrder.lineItems.edges,
      );

      const draftNameParam = draftOrder.name
        ? `&draft_name=${encodeURIComponent(String(draftOrder.name).replace(/^#/, ''))}`
        : '';
      const plan = { orderIdNumber, draftNameParam, addable, unavailable, capped };

      // Nothing sellable — there is no cart worth building. The cart is deliberately
      // left alone and the list stays on screen: clearing here would empty the rep's
      // cart for a draft that cannot open anyway.
      if (addable.length === 0) {
        setBlockedMessage(
          `This draft order can't be opened — none of its products are currently available for purchase.`,
        );
        setLoadingDraft(false);
        return;
      }

      // Confirm before mutating, so cancelling leaves the cart exactly as it was.
      if (unavailable.length > 0 || capped.length > 0) {
        setPendingOpen(plan);
        setLoadingDraft(false);
        return;
      }

      await openDraftInCart(plan);
    } catch (error) {
      reportOpenFailure(error);
    }
  };

  const confirmPendingOpen = async () => {
    if (!pendingOpen) return;

    setLoadingDraft(true);
    try {
      await openDraftInCart(pendingOpen);
    } catch (error) {
      setPendingOpen(null);
      reportOpenFailure(error);
    }
  };

  return (
    <div className="product-listing-page">
      <div className="draft-order-page--header">
        <h3 className="draft-order-page__title">Draft Orders</h3>
      </div>
  
      <div className="page-width">
      <div className="product-listing-page__container">
        {loading ? (
          <p>Loading draft orders...</p>
        ) : loadingDraft ? (
          <p>Loading draft order to cart...</p>
        ) : error ? (
          <div>
            <p>Error: {error.replace(' [RELOAD]', '')}</p>
            {error.includes('[RELOAD]') && (
              <p>
                <a 
                  href="/pages/draft-orders" 
                  style={{ textDecoration: 'underline', color: '#0066cc', cursor: 'pointer' }}
                >
                  Reload draft order list
                </a>
              </p>
            )}
          </div>
        ) : (
          <Fragment>
          {deleteError && (
            <div className="draft-order-page__message draft-order-page__message--error">
              {deleteError}
            </div>
          )}
          {blockedMessage && (
            <div className="draft-order-page__message draft-order-page__message--error">
              {blockedMessage}
            </div>
          )}
          <div className="product-listing-page__table-wrapper">
            <table className="product-listing-page__table">
              <thead>
                <tr>
                  <th>
                    <div className="product-listing-page__table-header">
                      Order
                    </div>
                  </th>
                  <th>
                    <div className="product-listing-page__table-header">
                      Date
                    </div>
                  </th>
                  <th>
                    <div className="product-listing-page__table-header">
                      Contacts 
                    </div>
                  </th>
                  <th>
                    <div className="product-listing-page__table-header">
                      Status
                    </div>
                  </th>
                  <th>
                    <div className="product-listing-page__table-header">
                      Total
                    </div>
                  </th>
                  <th>
                    <div className="product-listing-page__table-header"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {draftOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6">No draft orders found</td>
                  </tr>
                ) : (
                  draftOrders.map((order) => {
                    const orderIdNumber = order.id.split('/').pop();
                    const isCompleted = order.status === 'COMPLETED';
                    const isOpen = order.status === 'OPEN';
                    return (
                      <tr
                        className={
                          isCompleted
                            ? 'product-listing-page__table-row--noninteractive'
                            : 'product-listing-page__table-link'
                        }
                        key={order.id}
                        onClick={
                          isCompleted
                            ? undefined
                            : () => handleDraftOrderClick(orderIdNumber)
                        }
                        title={
                          isCompleted
                            ? 'Completed orders cannot be opened in the cart'
                            : undefined
                        }
                      >
                        <td className="product-listing-page__table-data">
                          {order.name}
                        </td>
                        <td className="product-listing-page__table-data">
                          {formatDate(order.createdAt)}
                        </td>
                        <td className="product-listing-page__table-data">
                          {(() => {
                            if (!order.customer) return 'N/A';
                            const { firstName, lastName, email } = order.customer;
                            if (firstName || lastName) {
                              return `${firstName || ''} ${lastName || ''}`.trim();
                            }
                            return email || 'N/A';
                          })()}
                        </td>
                        <td className="product-listing-page__table-data">
                          <div
                            className={`product-listing-page__table-data-status ${getStatusClass(order.status)}`}
                          >
                            {formatStatus(order.status)}
                          </div>
                        </td>
                        <td className="product-listing-page__table-data">
                          {order.totalPriceSet?.presentmentMoney ? (
                            <Fragment>
                              <span>
                                $
                                {Number(
                                  order.totalPriceSet.presentmentMoney.amount,
                                ).toFixed(2)}
                              </span>
                              <span>
                                {' '}
                                {
                                  order.totalPriceSet.presentmentMoney
                                    .currencyCode
                                }
                              </span>
                            </Fragment>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="product-listing-page__table-data product-listing-page__table-data--actions">
                          {isOpen && (
                            <button
                              type="button"
                              className="draft-order-delete-button"
                              onClick={(event) => {
                                // The row itself loads the draft into the cart.
                                event.stopPropagation();
                                setDeleteError(null);
                                setDeleteTarget(order);
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </Fragment>
        )}
      </div>
      </div>

      {pendingOpen && (
        <DraftOrderConfirmationModal
          title="Some products can't be added"
          message={
            <Fragment>
              Some products in this draft order can't be added to the cart as saved:
              {pendingOpen.unavailable.map((line) => (
                <Fragment key={`unavailable-${line.label}`}>
                  <br />
                  {`• ${line.label} — ${line.reason}`}
                </Fragment>
              ))}
              {pendingOpen.capped.map((line) => (
                <Fragment key={`capped-${line.label}`}>
                  <br />
                  {`• ${line.label} — only ${line.available} of ${line.requested} in stock`}
                </Fragment>
              ))}
              <br />
              <br />
              Everything else will be added. If you save this draft order afterwards, it
              will be updated to match the cart.
            </Fragment>
          }
          pendingTitle="Adding Draft Order To Cart"
          pendingMessage="Please wait..."
          confirmLabel="Continue"
          onCancel={() => setPendingOpen(null)}
          onContinue={confirmPendingOpen}
        />
      )}

      {deleteTarget && (
        <DraftOrderConfirmationModal
          title={`Delete draft order ${deleteTarget.name}?`}
          message="This permanently deletes the draft order. It cannot be undone."
          pendingTitle="Deleting Draft Order"
          pendingMessage="Please wait..."
          confirmLabel="Delete"
          destructive
          onCancel={() => setDeleteTarget(null)}
          onContinue={handleDeleteConfirm}
        />
      )}
    </div>
  );
}

export default DraftOrders;
